import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { queryClient } from "../api/queryClient";
import { useMemberStats, useUpdateOwnEmoji } from "../api/members-api";
import { BUILD_SHA, BUILD_STAMP } from "../buildInfo";
import { isDebugOn, toggleDebug } from "../components/dss/DebugOverlay";
import { Eyebrow, RoleBadge } from "../components/dss/Primitives";
import { Icon } from "../components/dss/Icon";
import { EMOJI_ONLY_ERROR, isEmojiOnly } from "../emoji";
import { useSupabaseAuth } from "../supabase/SupabaseContext";

const Me: React.FC = () => {
  const { member, roles, signOut, isViewingAsMember, canViewAsMember, setViewingAsMember, refreshMember } =
    useSupabaseAuth();
  const { data: stats } = useMemberStats(member?.id);
  const { mutateAsync: updateOwnEmoji, isLoading: isSavingEmoji } = useUpdateOwnEmoji();
  const navigate = useNavigate();
  const taps = useRef(0);
  const emojiInputRef = useRef<HTMLInputElement>(null);

  const [isEditingEmoji, setIsEditingEmoji] = useState(false);
  const [emojiDraft, setEmojiDraft] = useState("");
  const [emojiError, setEmojiError] = useState(false);

  if (!member) return null;

  const startEditingEmoji = () => {
    setEmojiDraft(member.emoji ?? "");
    setEmojiError(false);
    setIsEditingEmoji(true);
  };

  const saveEmoji = async () => {
    const trimmed = emojiDraft.trim();
    if (!trimmed || trimmed === member.emoji) {
      setIsEditingEmoji(false);
      setEmojiError(false);
      return;
    }
    if (!isEmojiOnly(trimmed)) {
      setEmojiError(true);
      // The blur that triggered this save already moved focus away —
      // bring it back so the user can fix the value immediately instead
      // of having to notice the error and click back in themselves.
      emojiInputRef.current?.focus();
      return;
    }
    await updateOwnEmoji(trimmed);
    await refreshMember();
    queryClient.invalidateQueries(["members"]);
    setIsEditingEmoji(false);
    setEmojiError(false);
  };

  const statRows = [
    { k: "Samlinger", v: stats?.eventsAttended ?? "–", sub: "DELTAGET" },
    { k: "Bøder", v: stats?.unpaidFines ?? "–", sub: "UBETALT" },
    { k: "Stemmer", v: stats?.votesCast ?? "–", sub: "AFGIVET" },
  ];

  const onSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <div className="mx-auto max-w-md min-h-app bg-paper px-7 pb-8 pt-[calc(0.5rem+env(safe-area-inset-top)+var(--view-as-offset))]">
      <div className="flex items-center justify-between">
        <Eyebrow>[ Min profil ]</Eyebrow>
      </div>
      <div className="flex items-center gap-4 mt-4">
        {isEditingEmoji ? (
          <div
            className={`w-[66px] h-[66px] rounded-full bg-paper2 border flex items-center justify-center flex-shrink-0 ${
              emojiError ? "border-clay" : "border-clay/50"
            }`}
          >
            <input
              ref={emojiInputRef}
              autoFocus
              value={emojiDraft}
              onChange={(e) => {
                setEmojiDraft(e.target.value);
                setEmojiError(false);
              }}
              onBlur={saveEmoji}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  setIsEditingEmoji(false);
                  setEmojiError(false);
                }
              }}
              maxLength={16}
              disabled={isSavingEmoji}
              className="w-full h-full bg-transparent text-[32px] text-center focus:outline-none"
            />
          </div>
        ) : (
          <button
            onClick={startEditingEmoji}
            className="relative w-[66px] h-[66px] rounded-full bg-paper2 border border-line-soft flex items-center justify-center text-[32px] flex-shrink-0"
            aria-label="Skift emoji"
          >
            {member.emoji}
            <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-clay border border-paper flex items-center justify-center text-paper">
              <Icon name="pen-nib" size={9} />
            </span>
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display text-[34px] leading-[0.92] tracking-tight font-extrabold uppercase">
            {member.name}
          </div>
          <div className="font-mono text-[10.5px] tracking-wide text-ink3 mt-1.5">{member.email}</div>
        </div>
      </div>
      {emojiError && (
        <div className="font-mono text-[10.5px] tracking-wide text-clay mt-2">{EMOJI_ONLY_ERROR}</div>
      )}
      <div className="flex flex-wrap gap-1.5 mt-3.5">
        <RoleBadge role="medlem" />
        {roles.map((r) => (
          <RoleBadge key={r} role={r} />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-5">
        {statRows.map((s) => (
          <div key={s.k} className="py-3 px-3 rounded-lg bg-card border border-line-soft">
            <div className="font-display font-extrabold text-[30px] leading-none tracking-tight tabular-nums">
              {s.v}
            </div>
            <div className="font-mono text-[9px] tracking-wide text-ink3 mt-1.5 uppercase">{s.k}</div>
            <div className="font-mono text-[8.5px] tracking-wide text-ink3 opacity-70 uppercase">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <Eyebrow className="mb-2.5">[ Konto ]</Eyebrow>
        <div className="rounded-lg bg-card border border-line-soft overflow-hidden">
          <div className="flex items-center gap-3 py-3.5 px-3.5">
            <Icon name="envelope" variant="regular" size={13} className="text-ink3 w-4" />
            <div className="flex-1 font-display text-[14px] font-bold uppercase tracking-tight">
              Skift mail
            </div>
            <div className="font-mono text-[9.5px] text-ink3">Kontakt admin</div>
          </div>
          <div className="flex items-center gap-3 py-3.5 px-3.5 border-t border-line-soft">
            <Icon name="shield-halved" size={13} className="text-ink3 w-4" />
            <div className="flex-1 font-display text-[14px] font-bold uppercase tracking-tight">
              Mine roller
            </div>
            <div className="font-mono text-[9.5px] text-ink3">{roles.length + 1} ROLLER</div>
          </div>
        </div>
      </div>

      {/* Lives on the profile because the Admin tab is hidden while the
          preview is on — this and the banner are the only ways back. */}
      {canViewAsMember && (
        <div className="mt-6">
          <Eyebrow className="mb-2.5">[ Admin ]</Eyebrow>
          <button
            onClick={() => setViewingAsMember(!isViewingAsMember)}
            className="w-full flex items-center gap-3 py-3.5 px-3.5 rounded-lg bg-card border border-line-soft text-left"
          >
            <Icon name="user" variant="regular" size={14} className="w-[18px] text-ink3" />
            <div className="flex-1">
              <div className="font-display text-sm font-bold uppercase tracking-tight">
                Se appen som medlem
              </div>
              <div className="text-[11.5px] text-ink3 mt-0.5 leading-snug">
                Skjuler dine roller, så du ser det samme som alle andre
              </div>
            </div>
            <div
              className={`w-[42px] h-6 rounded-full flex items-center px-0.5 flex-shrink-0 ${
                isViewingAsMember
                  ? "bg-clay justify-end"
                  : "bg-paper2 justify-start border border-line"
              }`}
            >
              <div
                className={`w-[18px] h-[18px] rounded-full ${
                  isViewingAsMember ? "bg-paper" : "bg-line"
                }`}
              />
            </div>
          </button>
        </div>
      )}

      <div className="mt-5">
        <button
          onClick={onSignOut}
          className="w-full py-3.5 rounded-lg border border-clay text-clay font-display text-[13px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
        >
          <Icon name="right-from-bracket" size={12} />
          <span>Log ud</span>
        </button>
      </div>

      {/* Which build this actually is. An installed app can sit on a cached
          version for days, and from the outside that looks exactly like a fix
          that didn't work — this tells the two apart. Three taps turns on a
          readout of the layout numbers, for when something looks wrong on a
          phone that can't be measured from here. */}
      <button
        onClick={() => {
          taps.current += 1;
          if (taps.current < 3) return;
          taps.current = 0;
          toggleDebug();
          window.location.reload();
        }}
        className="mt-6 w-full text-center font-mono text-[9.5px] tracking-wide text-ink3"
      >
        VERSION {BUILD_STAMP} · {BUILD_SHA}
        {isDebugOn() ? " · DEBUG TIL" : ""}
      </button>
    </div>
  );
};

export default Me;
